<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import kanbanCardsAPI from 'dashboard/api/kanbanCards';
import kanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  conversationId: { type: [Number, String], required: false },
});

const { t } = useI18n();
const route = useRoute();

const products = ref([]);
const total = ref(0);
const isLoading = ref(false);
const pipelines = ref([]);

// ✅ conversationId reativo + normalizado (Number)
// aceita prop ou route.params.conversationId (se existir na rota)
const conversationId = computed(() => {
  const fromRoute = route.params.conversationId;
  const fromProps = props.conversationId;

  const id = fromProps ?? fromRoute;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
});

const formattedTotal = computed(() => {
  return `R$ ${total.value.toFixed(2).replace('.', ',')}`;
});

const toNumber = v => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const loadProducts = async () => {
  console.log('🔄 loadProducts called for conversation:', conversationId.value);

  if (!conversationId.value) {
    console.log('❌ No conversation ID, returning.');
    return;
  }

  isLoading.value = true;
  products.value = [];
  total.value = 0;

  try {
    // 1) pipelines
    const pipelinesResponse = await kanbanAPI.getPipelines();
    pipelines.value = pipelinesResponse.data || [];

    // 2) cards de cada pipeline
    for (const pipeline of pipelines.value) {
      try {
        const cardsResponse = await kanbanCardsAPI.getCards(pipeline.id);
        const cards = cardsResponse.data || [];

        // ✅ filtro por conversationId
        const conversationCards = cards.filter(card => {
          return (card.conversations || []).some(conv => {
            return Number(conv.id) === conversationId.value;
          });
        });

        for (const card of conversationCards) {
          const cardProducts = card.kanban_card_products || [];
          if (!cardProducts.length) continue;

          for (const cardProduct of cardProducts) {
            const lineTotal = toNumber(cardProduct.total_price);

            products.value.push({
              ...cardProduct,
              kanban_card_id: card.id,
              kanban_pipeline_id: pipeline.id,
              pipeline_name: pipeline.name,
            });

            total.value += lineTotal;
          }
        }
      } catch (error) {
        console.error(
          `❌ Erro ao carregar cards do pipeline ${pipeline.id}:`,
          error
        );
      }
    }

    console.log('🎯 Final products:', products.value);
    console.log('💰 Final total:', total.value);
  } catch (error) {
    console.error('❌ Erro ao carregar produtos da conversa:', error);
  } finally {
    isLoading.value = false;
  }
};

// ✅ eventos (iguais ao ContactProducts)
const handleKanbanConversationMoved = event => {
  const { conversationId: movedConversationId } = event.detail || {};
  console.log('🔔 WebSocket: Kanban conversation moved', movedConversationId);

  // se vier id, recarrega somente quando for a conversa atual
  if (
    !movedConversationId ||
    Number(movedConversationId) === conversationId.value
  ) {
    loadProducts();
  }
};

const handleProductsUpdate = event => {
  const { action, conversationId: updatedConversationId } = event.detail || {};
  console.log('🔔 Products updated', action, updatedConversationId);

  if (
    action === 'product_added' ||
    action === 'product_updated' ||
    action === 'product_removed'
  ) {
    if (
      !updatedConversationId ||
      Number(updatedConversationId) === conversationId.value
    ) {
      loadProducts();
    }
  }
};

const handleKanbanProductAdded = event => {
  console.log(
    '🔔 WebSocket: Kanban product added in ConversationFunnelProducts',
    event.detail
  );
  loadProducts();
};
const handleKanbanProductUpdated = event => {
  console.log(
    '🔔 WebSocket: Kanban product updated in ConversationFunnelProducts',
    event.detail
  );
  loadProducts();
};
const handleKanbanProductRemoved = event => {
  console.log(
    '🔔 WebSocket: Kanban product removed in ConversationFunnelProducts',
    event.detail
  );
  loadProducts();
};

onMounted(() => {
  loadProducts();

  window.addEventListener(
    'kanbanConversationMoved',
    handleKanbanConversationMoved
  );
  window.addEventListener('productsUpdated', handleProductsUpdate);
  window.addEventListener('kanbanProductAdded', handleKanbanProductAdded);
  window.addEventListener('kanbanProductUpdated', handleKanbanProductUpdated);
  window.addEventListener('kanbanProductRemoved', handleKanbanProductRemoved);
});

onUnmounted(() => {
  window.removeEventListener(
    'kanbanConversationMoved',
    handleKanbanConversationMoved
  );
  window.removeEventListener('productsUpdated', handleProductsUpdate);
  window.removeEventListener('kanbanProductAdded', handleKanbanProductAdded);
  window.removeEventListener(
    'kanbanProductUpdated',
    handleKanbanProductUpdated
  );
  window.removeEventListener(
    'kanbanProductRemoved',
    handleKanbanProductRemoved
  );
});

// ✅ watch funciona porque conversationId é computed
watch(conversationId, (newId, oldId) => {
  console.log('👀 Conversation ID changed:', oldId, '=>', newId);
  if (newId) loadProducts();
});
</script>

<template>
  <div class="conversation-funnel-products py-4 px-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.PRODUCTS.TITLE') }}
      </h3>

      <div
        v-if="!isLoading && products.length > 0"
        class="text-sm font-medium text-n-slate-11"
      >
        {{ formattedTotal }}
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <div
        class="animate-spin rounded-full h-6 w-6 border-b-2 border-n-blue-9"
      />
    </div>

    <div v-else-if="products.length === 0" class="text-center py-8">
      <div class="text-n-slate-10 mb-2">
        <svg
          class="w-12 h-12 mx-auto"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      </div>

      <p class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.PRODUCTS.EMPTY_STATE') }}
      </p>
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="p in products"
        :key="`${p.kanban_card_id}-${p.product_id || p.product?.id || p.id}`"
        class="flex items-center justify-between p-3 bg-n-alpha-black2 rounded-lg border border-n-strong"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <div
              class="w-3 h-3 rounded-full"
              :style="{ backgroundColor: p.product?.color || '#999' }"
            />

            <h4 class="text-sm font-medium text-n-slate-12 truncate">
              {{ p.product?.name || `Produto #${p.product_id}` }}
            </h4>
          </div>

          <div class="flex items-center gap-2 text-xs text-n-slate-11">
            <span>{{ p.quantity || 0 }}x</span>
            <span>{{
              p.product?.formatted_price || p.formatted_price || ''
            }}</span>

            <span
              v-if="
                p.unit_price &&
                p.product?.base_price &&
                String(p.unit_price) !== String(p.product.base_price)
              "
              class="text-n-slate-10"
            >
              ({{ p.currency }} {{ p.unit_price }})
            </span>
          </div>

          <div class="text-xs text-n-slate-10 mt-1">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.PRODUCTS.FROM_CARD') }}
            #{{ p.kanban_card_id }} - {{ p.pipeline_name }}
          </div>
        </div>

        <div class="text-right">
          <div class="text-sm font-medium text-n-slate-12">
            {{
              p.formatted_total ||
              `R$ ${Number(p.total_price || 0)
                .toFixed(2)
                .replace('.', ',')}`
            }}
          </div>
        </div>
      </div>

      <div v-if="products.length > 1" class="pt-3 border-t border-n-strong">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.PRODUCTS.TOTAL') }}
            ({{ products.length }}
            {{ t('CONTACTS_LAYOUT.SIDEBAR.PRODUCTS.PRODUCTS') }})
          </span>

          <span class="text-sm font-bold text-n-slate-12">
            {{ formattedTotal }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
